// @flow
import { useRef, useEffect } from "react";
import { dequal as deepEqual } from "dequal/lite";

type Callback = () => void | (() => void);
type DependencyList = $ReadOnlyArray<any>;

/**
 * Validates that dependencies are used correctly.
 * Deep compare should not be used with empty arrays or no dependencies.
 */
function checkDeps(deps: DependencyList) {
  if (process.env.NODE_ENV !== "production") {
    if (!deps || deps.length === 0) {
      console.warn(
        "useDeepCompareEffect should not be used with no dependencies. Use React.useEffect instead.",
      );
    }
  }
}

const useDeepCompareMemoize = (value: DependencyList): DependencyList => {
  const ref = useRef<DependencyList>(value);

  // Only update the ref if the deep equality check fails
  if (!deepEqual(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
};

/**
 * A version of useEffect that accepts a deep comparison of dependencies.
 */
export function useDeepCompareEffect(
  callback: Callback,
  dependencies: DependencyList,
): void {
  if (process.env.NODE_ENV !== "production") {
    checkDeps(dependencies);
  }

  // We use the memoized ref as the dependency.
  // React's shallow check will only trigger when the ref object itself changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(callback, useDeepCompareMemoize(dependencies));
}

export default useDeepCompareEffect;
